import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import GateChecklist from "@/components/GateChecklist";
import {
  Plus, Loader2, Camera, ShieldCheck, MapPin, FileSignature, Radio, PartyPopper,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Booking", "KYC", "Agreement", "Handover"];
const PONDY = { lat: 11.9139, long: 79.8145 };
const SIDES = ["front", "back", "left", "right"];

function StepDots({ current }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
            i <= current ? "bg-tenant-primary text-tenant-primary-foreground" : "bg-muted text-muted-foreground"
          }`}>{i + 1}</div>
          {i < STEPS.length - 1 && <div className={`h-0.5 w-6 ${i < current ? "bg-tenant-primary" : "bg-muted"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function RentalWizard({ vehicles, onDone }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null); // serialize response once booking exists
  const [busy, setBusy] = useState(false);
  const stage = data?.booking?.stage;
  const stepIndex = !data ? 0 : { kyc: 1, agreement: 2, handover: 3, active: 3 }[stage] ?? 0;

  const available = vehicles.filter((v) => v.status === "available");

  // ----- form states -----
  const [b, setB] = useState({ vehicle_id: "", customer_name: "", customer_phone: "", window_start: "", window_end: "", payment_model: "weekly" });
  const [k, setK] = useState({ aadhaar: "", dl: "", selfie_captured: false, gname: "", gphone: "", grelation: "", address: "", address_verified: false, tenure: "experienced", employment: "full_time", residency: "local", platform_standing: "good", payment_history: "on_time", guarantor_strength: "strong", prior_damage: false });
  const [a, setA] = useState({ signature_name: "", insurance_acknowledged: false, deposit_amount: "", advance_amount: "" });
  const [h, setH] = useState({ photos: [], selfie_match: false, odometer_out: "", charge_out: "", battery_serial_out: "", charger_serial_out: "", payment_cutoff_hours: 168, staff_confirm: false, gps_device_id: "" });

  const reset = () => { setData(null); setB({ vehicle_id: "", customer_name: "", customer_phone: "", window_start: "", window_end: "", payment_model: "weekly" }); setK({ aadhaar: "", dl: "", selfie_captured: false, gname: "", gphone: "", grelation: "", address: "", address_verified: false }); setA({ signature_name: "", insurance_acknowledged: false, deposit_amount: "", advance_amount: "" }); setH({ photos: [], selfie_match: false, odometer_out: "", charge_out: "", battery_serial_out: "", charger_serial_out: "", payment_cutoff_hours: 168, staff_confirm: false, gps_device_id: "" }); };

  const handleError = (e) => toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong");

  const createBooking = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/operator/bookings", b);
      setData(res);
      setA((s) => ({ ...s, deposit_amount: res.booking.deposit_required }));
    } catch (e) { handleError(e); } finally { setBusy(false); }
  };
  const submitKyc = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.patch(`/operator/bookings/${data.booking.id}/kyc`, {
        aadhaar: k.aadhaar, dl: k.dl, selfie_captured: k.selfie_captured,
        guarantor: { name: k.gname, phone: k.gphone, relation: k.grelation },
        address: k.address, address_verified: k.address_verified,
        tenure: k.tenure, employment: k.employment, residency: k.residency,
        platform_standing: k.platform_standing, payment_history: k.payment_history,
        guarantor_strength: k.guarantor_strength, prior_damage: k.prior_damage,
      });
      setData(res);
    } catch (e) { handleError(e); } finally { setBusy(false); }
  };
  const submitAgreement = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.patch(`/operator/bookings/${data.booking.id}/agreement`, {
        signature_name: a.signature_name, insurance_acknowledged: a.insurance_acknowledged,
        deposit_amount: Number(a.deposit_amount), advance_amount: Number(a.advance_amount),
      });
      setData(res);
    } catch (e) { handleError(e); } finally { setBusy(false); }
  };
  const pairGps = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.patch(`/operator/bookings/${data.booking.id}/gps-pair`, { gps_device_id: h.gps_device_id || "GPS-AUTO" });
      setData(res); toast.success("GPS paired & pinging");
    } catch (e) { handleError(e); } finally { setBusy(false); }
  };
  const completeHandover = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/operator/bookings/${data.booking.id}/handover`, {
        photo_sides: h.photos, photo_geo: h.photos.length ? PONDY : {},
        selfie_match: h.selfie_match, odometer_out: h.odometer_out === "" ? null : Number(h.odometer_out),
        charge_out: h.charge_out === "" ? null : Number(h.charge_out),
        battery_serial_out: h.battery_serial_out, charger_serial_out: h.charger_serial_out,
        payment_cutoff_hours: Number(h.payment_cutoff_hours), staff_confirm: h.staff_confirm,
      });
      setData(res.detail);
      if (res.activated) { toast.success("Handover complete — rental active"); }
      else { toast.error("Blocked: " + res.blockers.join(", ")); }
    } catch (e) { handleError(e); } finally { setBusy(false); }
  };

  const batteryGate = data?.gates?.find((g) => g.key === "battery_serial");
  const gpsGate = data?.gates?.find((g) => g.key === "gps_paired");

  const close = (o) => { setOpen(o); if (!o) { reset(); onDone?.(); } };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <Button data-testid="new-rental-btn" className="gap-2 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
          <Plus className="h-4 w-4" /> New rider rental
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Rider rental — guided pipeline</DialogTitle>
          <DialogDescription>Every gate is enforced. No step can be skipped.</DialogDescription>
        </DialogHeader>
        <div className="pb-2"><StepDots current={stepIndex} /></div>

        {/* Stage 1 — Booking */}
        {!data && (
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Vehicle</Label>
              <select data-testid="rental-vehicle" value={b.vehicle_id} onChange={(e) => setB({ ...b, vehicle_id: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Select an available vehicle…</option>
                {available.map((v) => <option key={v.id} value={v.id}>{v.reg_number} · {v.model}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Rider name</Label>
                <Input data-testid="rental-customer-name" value={b.customer_name} onChange={(e) => setB({ ...b, customer_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label>
                <Input data-testid="rental-customer-phone" value={b.customer_phone} onChange={(e) => setB({ ...b, customer_phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Window start</Label>
                <Input data-testid="rental-window-start" type="date" value={b.window_start} onChange={(e) => setB({ ...b, window_start: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Window end</Label>
                <Input data-testid="rental-window-end" type="date" value={b.window_end} onChange={(e) => setB({ ...b, window_end: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Payment model</Label>
              <select data-testid="rental-payment-model" value={b.payment_model} onChange={(e) => setB({ ...b, payment_model: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="weekly">Weekly (default)</option>
                <option value="daily">Daily</option>
                <option value="advance_monthly">Advance monthly</option>
                <option value="deposit_monthly">Deposit + monthly</option>
              </select>
            </div>
            <Button data-testid="rental-create-submit" onClick={createBooking} disabled={busy || !b.vehicle_id || !b.customer_name || !b.customer_phone}
              className="w-full gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Lock vehicle & continue</Button>
          </div>
        )}

        {/* Stage 2 — KYC */}
        {stage === "kyc" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Aadhaar number</Label>
                <Input data-testid="kyc-aadhaar" value={k.aadhaar} onChange={(e) => setK({ ...k, aadhaar: e.target.value })} placeholder="XXXX XXXX XXXX" /></div>
              <div className="space-y-1.5"><Label>Driving licence</Label>
                <Input data-testid="kyc-dl" value={k.dl} onChange={(e) => setK({ ...k, dl: e.target.value })} /></div>
            </div>
            <Button type="button" variant={k.selfie_captured ? "default" : "outline"} data-testid="kyc-selfie"
              onClick={() => setK({ ...k, selfie_captured: true })} className="w-full gap-2">
              <Camera className="h-4 w-4" /> {k.selfie_captured ? "Selfie captured · face-match ok" : "Capture selfie & face-match"}
            </Button>
            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-sm font-medium">Guarantor</p>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="kyc-guarantor-name" placeholder="Name" value={k.gname} onChange={(e) => setK({ ...k, gname: e.target.value })} />
                <Input data-testid="kyc-guarantor-phone" placeholder="Phone" value={k.gphone} onChange={(e) => setK({ ...k, gphone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5"><Label>Address</Label>
              <Input data-testid="kyc-address" value={k.address} onChange={(e) => setK({ ...k, address: e.target.value })} /></div>
            <Button type="button" variant={k.address_verified ? "default" : "outline"} data-testid="kyc-verify-address"
              onClick={() => setK({ ...k, address_verified: true })} className="w-full gap-2">
              <MapPin className="h-4 w-4" /> {k.address_verified ? "Address verified" : "Verify address"}
            </Button>

            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-sm font-medium">Risk profile</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[
                  ["tenure", "Tenure", [["experienced", "Experienced >6m"], ["mid", "2wk–6m"], ["new", "New <2wk"]]],
                  ["employment", "Employment", [["full_time", "Full-time"], ["part_time", "Part-time/gig"], ["none", "None"]]],
                  ["residency", "Residency", [["local", "Local"], ["migrant", "Migrant"]]],
                  ["guarantor_strength", "Guarantor", [["strong", "Verified local"], ["weak", "Friend only"], ["none", "None"]]],
                  ["platform_standing", "Platform standing", [["good", "Good rating"], ["new", "New account"], ["flagged", "Prior suspension"]]],
                  ["payment_history", "Payment history", [["on_time", "On-time"], ["none", "No history"], ["default", "Prior default"]]],
                ].map(([key, label, opts]) => (
                  <label key={key} className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground">{label}</span>
                    <select data-testid={`kyc-${key}`} value={k[key]} onChange={(e) => setK({ ...k, [key]: e.target.value })}
                      className="rounded-md border border-input bg-background px-2 py-1.5">
                      {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <label className="mt-2 flex items-center justify-between text-sm">
                Prior uncovered damage
                <Switch data-testid="kyc-prior-damage" checked={k.prior_damage} onCheckedChange={(v) => setK({ ...k, prior_damage: v })} />
              </label>
            </div>

            <Button data-testid="kyc-submit" onClick={submitKyc} disabled={busy} className="w-full gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} <ShieldCheck className="h-4 w-4" /> Pass KYC & continue
            </Button>
          </div>
        )}

        {/* Stage 3 — Agreement & deposit */}
        {stage === "agreement" && (
          <div className="space-y-3">
            <div className={`rounded-lg border p-3 text-sm ${
              data.booking.risk_band === "high" ? "border-status-recovery/40 bg-status-recovery/10"
              : data.booking.risk_band === "medium" ? "border-status-overdue/40 bg-status-overdue/10"
              : "border-status-available/40 bg-status-available/10"}`} data-testid="risk-banner">
              Risk band: <span className="font-semibold capitalize">{data.booking.risk_band || "—"}</span>
              {data.booking.risk_total != null && <span className="ml-1 text-muted-foreground">({data.booking.risk_total} pts)</span>}
              <span className="ml-2">· deposit ×{data.booking.deposit_multiplier}</span>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              Deposit required: <span className="font-semibold">₹{data.booking.deposit_required.toLocaleString()}</span>
              <span className="ml-2 text-xs text-muted-foreground">base ₹{(data.booking.deposit_base || 0).toLocaleString()} × {data.booking.deposit_multiplier}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Deposit collected (₹)</Label>
                <Input data-testid="agreement-deposit" type="number" value={a.deposit_amount} onChange={(e) => setA({ ...a, deposit_amount: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Advance rent (₹)</Label>
                <Input data-testid="agreement-advance" type="number" value={a.advance_amount} onChange={(e) => setA({ ...a, advance_amount: e.target.value })} placeholder="first cycle" /></div>
            </div>
            <div className="space-y-1.5"><Label>Signature (rider name)</Label>
              <Input data-testid="agreement-signature" value={a.signature_name} onChange={(e) => setA({ ...a, signature_name: e.target.value })} /></div>
            <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
              Insurance acknowledged
              <Switch data-testid="agreement-insurance" checked={a.insurance_acknowledged} onCheckedChange={(v) => setA({ ...a, insurance_acknowledged: v })} />
            </label>
            <Button data-testid="agreement-submit" onClick={submitAgreement} disabled={busy} className="w-full gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} <FileSignature className="h-4 w-4" /> Sign, hold deposit & continue
            </Button>
          </div>
        )}

        {/* Stage 4 — Handover */}
        {stage === "handover" && (
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium">Gate checklist (server-enforced)</p>
              <GateChecklist gates={data.gates} />
            </div>

            {gpsGate?.enabled && (
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1.5"><Label>GPS device id</Label>
                  <Input data-testid="handover-gps-id" value={h.gps_device_id} onChange={(e) => setH({ ...h, gps_device_id: e.target.value })} placeholder="GPS-001" /></div>
                <Button type="button" variant="outline" data-testid="handover-pair-gps" onClick={pairGps} disabled={busy} className="gap-1.5">
                  <Radio className="h-4 w-4" /> Pair
                </Button>
              </div>
            )}

            <div>
              <Label className="mb-1.5 block">Condition photos (all sides)</Label>
              <div className="grid grid-cols-4 gap-2">
                {SIDES.map((side) => {
                  const captured = h.photos.includes(side);
                  return (
                    <button key={side} type="button" data-testid={`handover-photo-${side}`}
                      onClick={() => setH((s) => ({ ...s, photos: captured ? s.photos.filter((p) => p !== side) : [...s.photos, side] }))}
                      className={`flex flex-col items-center gap-1 rounded-lg border py-3 text-xs capitalize ${
                        captured ? "border-tenant-primary bg-tenant-primary/10 text-tenant-primary" : "border-border text-muted-foreground"
                      }`}>
                      <Camera className="h-4 w-4" /> {side}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Odometer (km)</Label>
                <Input data-testid="handover-odometer" type="number" value={h.odometer_out} onChange={(e) => setH({ ...h, odometer_out: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Charge %</Label>
                <Input data-testid="handover-charge" type="number" value={h.charge_out} onChange={(e) => setH({ ...h, charge_out: e.target.value })} /></div>
            </div>

            {batteryGate?.enabled && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Battery serial</Label>
                  <Input data-testid="handover-battery-serial" value={h.battery_serial_out} onChange={(e) => setH({ ...h, battery_serial_out: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Charger serial</Label>
                  <Input data-testid="handover-charger-serial" value={h.charger_serial_out} onChange={(e) => setH({ ...h, charger_serial_out: e.target.value })} /></div>
              </div>
            )}

            <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
              Handover selfie matches registered rider
              <Switch data-testid="handover-selfie-match" checked={h.selfie_match} onCheckedChange={(v) => setH({ ...h, selfie_match: v })} />
            </label>
            <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
              Staff confirmation
              <Switch data-testid="handover-staff-confirm" checked={h.staff_confirm} onCheckedChange={(v) => setH({ ...h, staff_confirm: v })} />
            </label>

            <Button data-testid="handover-submit" onClick={completeHandover} disabled={busy} className="w-full gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Complete handover
            </Button>
          </div>
        )}

        {/* Active — success */}
        {stage === "active" && (
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="rental-active-state">
            <PartyPopper className="h-10 w-10 text-tenant-primary" />
            <p className="text-lg font-semibold">Rental is active</p>
            <p className="text-sm text-muted-foreground">All nine gates passed. Vehicle handed over and rented.</p>
            <Button data-testid="rental-done" onClick={() => close(false)} className="mt-2">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
